'use client';

import { useState, useEffect, useRef } from 'react';

interface PronunciationButtonProps {
  word: string;
  className?: string;
}

export default function PronunciationButton({ word, className = '' }: PronunciationButtonProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const selectedVoiceRef = useRef<SpeechSynthesisVoice | null>(null);

  useEffect(() => {
    // Load available voices and select the best one
    const loadVoices = () => {
      if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
        return;
      }

      const voices = window.speechSynthesis.getVoices();
      
      // Prefer high-quality voices in this order:
      // 1. Google UK English Female (very clear, natural)
      // 2. Microsoft Zira (clear, lower pitch)
      // 3. Any UK English female voice
      // 4. Any US English female voice with lower pitch
      // 5. Fallback to first available English voice
      
      const preferredVoiceNames = [
        'Google UK English Female',
        'Microsoft Zira',
        'Microsoft Hazel',
        'Samantha',
        'Victoria',
      ];

      let selectedVoice = voices.find(voice => 
        preferredVoiceNames.some(name => voice.name.includes(name))
      );

      // If no preferred voice found, look for UK English female
      if (!selectedVoice) {
        selectedVoice = voices.find(voice => 
          voice.lang.startsWith('en-GB') && 
          (voice.name.toLowerCase().includes('female') || voice.name.toLowerCase().includes('zira') || voice.name.toLowerCase().includes('hazel'))
        );
      }

      // If still no voice, look for any English female voice
      if (!selectedVoice) {
        selectedVoice = voices.find(voice => 
          voice.lang.startsWith('en') && 
          (voice.name.toLowerCase().includes('female') || voice.name.toLowerCase().includes('zira') || voice.name.toLowerCase().includes('hazel'))
        );
      }

      // Fallback to first English voice
      if (!selectedVoice) {
        selectedVoice = voices.find(voice => voice.lang.startsWith('en')) || voices[0];
      }

      selectedVoiceRef.current = selectedVoice || null;
    };

    // Voices may not be immediately available, so we need to listen for the voiceschanged event
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      loadVoices();
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, []);

  const handlePlay = () => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      return;
    }

    // Stop any currently playing speech
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(word);
    
    // Use the selected voice if available
    if (selectedVoiceRef.current) {
      utterance.voice = selectedVoiceRef.current;
    } else {
      utterance.lang = 'en-US'; // Prefer UK English for better pronunciation
    }
    
    // Lower pitch for clearer, less high-pitched sound
    utterance.pitch = 0.75; // Lower pitch (default is 1, range is 0-2)
    utterance.rate = 0.85; // Slightly slower for clarity
    utterance.volume = 1;

    utterance.onstart = () => setIsPlaying(true);
    utterance.onend = () => setIsPlaying(false);
    utterance.onerror = () => setIsPlaying(false);

    window.speechSynthesis.speak(utterance);
  };

  return (
    <button
      onClick={handlePlay}
      className={`inline-flex items-center justify-center w-5 h-5 ml-2 text-slate-400 hover:text-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-[#257676]/20 focus:ring-offset-1 rounded ${className}`}
      aria-label={`Pronounce ${word}`}
      type="button"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className={`w-4 h-4 ${isPlaying ? 'text-[#257676]' : ''}`}
      >
        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
      </svg>
    </button>
  );
}

